import { useDisclosure, useForceUpdate, useListState, useMediaQuery, useViewportSize } from "@mantine/hooks";
import { ActionIcon, Button, Menu, ScrollArea, SegmentedControl, Text } from "@mantine/core";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { IconDots, IconPencil, IconRefresh, IconSettings, IconTrash } from "@tabler/icons-react";
import cn from "classnames";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdvancedSettings, PlayerColor, Position } from "../models";
import { Chess } from "chess.js";
import AddPositionModal from "./AddPositionModal";
import SaveModal from "./SaveModal";
import EditPositionModal from "./EditPositionModal";
import ResetPositionModal from "./ResetPositionModal";
import axios from "axios";
import { usePersistedStorage, useSettingsStorage, useStorage } from "../ZustandState";
import { putPositions } from "./fetchPositions";
import { useQueryClient } from "react-query";
import DeletePositionModal from "./DeletePositionModal";
import { INITIAL_POSITION } from "../utils";

interface Props {
    setSelectedPosition: (position: Position) => void;
    selectedPosition: Position;
    game: Chess;
    setPlayerColor: (color: PlayerColor) => void;
    playerColor: PlayerColor;
    positions: Position[];
    openedAddPositionModal: boolean;
    closeAddPositionModal: () => void;
    openAddPositionModal: () => void;
}

const PositionsSidePanel: React.FC<Props> = ({
    game,
    setSelectedPosition,
    selectedPosition,
    setPlayerColor,
    playerColor,
    positions,
    openedAddPositionModal,
    closeAddPositionModal,
    openAddPositionModal,
}) => {
    const [state, handlers] = useListState<Position>(positions);
    const [initialPositionList, setInitialPositionList] = useState(positions);
    const navigate = useNavigate();
    const forceUpdate = useForceUpdate();

    const [openedResetModal, { open: openResetModal, close: closeResetModal }] = useDisclosure(false);
    const [openedSaveModal, { open: openSaveModal, close: closeSaveModal }] = useDisclosure(false);
    const [openedDeleteModal, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
    const settings = useSettingsStorage((state) => state);
    const setSelectedGlobalPositionID = usePersistedStorage((state) => state.setSelectedPositionID);
    const [shouldSave, setShouldSave] = useState(false);
    const queryClient = useQueryClient();
    const openedEditPositionModal = useStorage((state) => state.openedEditModal);
    const setOpenEditModal = useStorage((state) => state.setOpenedEditModal);
    const isMobile = useMediaQuery("(max-width: 1024px)");

    const openingsToDisplay = state.filter((position) => position.defaultPlayerColor === playerColor);
    const selectedIndex = openingsToDisplay.findIndex((position) => position.id === selectedPosition.id);

    useEffect(() => {
        handlers.setState(positions);
        setInitialPositionList(positions);
    }, [positions]);

    const resetToInitialPosition = () => {
        setSelectedPosition(INITIAL_POSITION);
    };

    const items = openingsToDisplay.map((item, index) => (
        <Draggable key={index.toString()} index={index} draggableId={index.toString()} isDragDisabled={isMobile}>
            {(provided, snapshot) => (
                <div
                    {...provided.dragHandleProps}
                    onClick={() => setSelectedPosition(item)}
                    className={cn("flex items-center pl-3 pr-5 py-2 mb-1  cursor-pointer text-start justify-between", {
                        "shadow-md": snapshot.isDragging,
                        "text-[#a5d8ff] bg-[#1971c234]": selectedIndex === index,
                        "bg-[#1f2025]": selectedIndex !== index,
                    })}
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                >
                    <div>
                        <span>{item.name}</span>
                    </div>
                    <Menu shadow="md" width={200} withArrow closeOnEscape>
                        <Menu.Target>
                            <ActionIcon
                                onClick={(e) => {
                                    e.stopPropagation();
                                }}
                                variant="transparent"
                                color="gray"
                            >
                                <IconDots />
                            </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                            <Menu.Item
                                leftSection={<IconSettings size={14} />}
                                onClick={() => {
                                    navigate(`/exclusions/?position=${item.id}`);
                                }}
                            >
                                Advanced: Exclude some moves
                            </Menu.Item>
                            <Menu.Item
                                leftSection={<IconPencil size={14} />}
                                onClick={() => {
                                    setOpenEditModal(true);
                                }}
                            >
                                Edit
                            </Menu.Item>
                            <Menu.Item
                                color="yellow"
                                leftSection={<IconRefresh size={14} />}
                                onClick={(e) => {
                                    openResetModal();
                                }}
                            >
                                Reset rating...
                            </Menu.Item>

                            <Menu.Item
                                color="red"
                                leftSection={<IconTrash size={14} />}
                                onClick={(e) => {
                                    openDeleteModal();
                                }}
                            >
                                Delete
                            </Menu.Item>
                        </Menu.Dropdown>
                    </Menu>
                </div>
            )}
        </Draggable>
    ));

    const handleOnAddPosition = async (name: string, advancedSettings: AdvancedSettings) => {
        handlers.append({
            eco: selectedPosition.eco,
            fen: selectedPosition.fen,
            name,
            defaultPlayerColor: playerColor,
            pgn: game?.pgn(),
            ...advancedSettings,
        });
        resetToInitialPosition();
        setShouldSave(true);
    };

    const handleOnEditPosition = async (name: string, advancedSettings: AdvancedSettings) => {
        handlers.setItem(
            state.findIndex((position) => _.isEqual(position, selectedPosition)),
            { ...selectedPosition, name, ...advancedSettings },
        );
        resetToInitialPosition();
        setShouldSave(true);
        setSelectedGlobalPositionID(undefined);
    };

    const handleOnResetPosition = async (position: Position, shouldDeleteGames: boolean) => {
        const response = await axios.post<unknown, { data: any; status: number }>(
            `${import.meta.env.VITE_OPENING_TRAINER_BACKEND_URL}/reset_position_rating`,
            { positionId: position.id, startRating: settings.startRating, shouldDeleteGames },
            {
                withCredentials: true,
            },
        );
        if (response.status === 200) {
            setSelectedGlobalPositionID(position.id);
        }

        queryClient.invalidateQueries({ queryKey: ["positions"] });
    };

    const handleOnDeletePosition = async (position: Position) => {
        const itemIndex = state.findIndex((pos) => pos.id === position.id);
        if (itemIndex === -1) return;
        handlers.remove(itemIndex);
        resetToInitialPosition();
        setShouldSave(true);
    };

    const handleOnSave = async () => {
        forceUpdate();

        const mappedPositions = state.map((pos) => ({ ...pos, originalName: undefined }));
        const whitePositions = mappedPositions
            .filter((pos) => pos.defaultPlayerColor === "w")
            .map((pos, index) => ({ ...pos, index: index + 1 }));
        const blackPositions = mappedPositions
            .filter((pos) => pos.defaultPlayerColor === "b")
            .map((pos, index) => ({ ...pos, index: index + 1 }));
        const positionsToSend = [...whitePositions, ...blackPositions];
        await putPositions(positionsToSend, settings.startRating);

        setSelectedGlobalPositionID(undefined);

        console.log("SAVED");
    };

    const isInitialPosition = selectedPosition.name === "Initial position";

    const { width } = useViewportSize();

    const getScrollAreaHeight = () => {
        switch (true) {
            case width > 1200:
                return "29rem";
            case width > 1130:
                return "26rem";
            case width > 1080:
                return "23rem";
            case width > 1030:
                return "21rem";
            default:
                return "20rem";
                break;
        }
    };

    useEffect(() => {
        const asyncFunc = async () => {
            if (shouldSave) {
                await handleOnSave();
                setShouldSave(false);
                queryClient.invalidateQueries({ queryKey: ["positions"] });
            }
        };

        asyncFunc();
    }, [state]);

    const hasInitialPosition = state.some(
        (position) => position.defaultPlayerColor === playerColor && position.name === "Initial position",
    );
    const isExperiencedUser = !hasInitialPosition || state.length >= 5;

    return (
        <div className="bg-[#25262b] w-[400px] [@media(max-width:1023px)]:w-full px-6 py-4">
            {openedAddPositionModal && (
                <AddPositionModal
                    name={selectedPosition.name}
                    opened={openedAddPositionModal}
                    close={closeAddPositionModal}
                    onAddPosition={handleOnAddPosition}
                    playerColor={playerColor}
                    positions={positions}
                />
            )}
            {openedEditPositionModal && (
                <EditPositionModal
                    position={selectedPosition}
                    opened={openedEditPositionModal}
                    close={() => setOpenEditModal(false)}
                    onEditPosition={handleOnEditPosition}
                    playerColor={playerColor}
                />
            )}
            {openedResetModal && (
                <ResetPositionModal
                    position={selectedPosition}
                    opened={openedResetModal}
                    close={closeResetModal}
                    onResetPosition={handleOnResetPosition}
                />
            )}
            {openedDeleteModal && (
                <DeletePositionModal
                    position={selectedPosition}
                    opened={openedDeleteModal}
                    close={closeDeleteModal}
                    onDeletePosition={handleOnDeletePosition}
                    numPositionsOfColor={openingsToDisplay.length}
                />
            )}
            <SaveModal opened={openedSaveModal} close={closeSaveModal} positions={state} />
            <div className="h-full relative flex flex-col justify-between mt-4">
                <div className="mt-1">
                    <SegmentedControl
                        w="12rem"
                        className="mt-1"
                        styles={{
                            root: {
                                backgroundColor: "#1c1d21",
                            },
                        }}
                        color="blue"
                        value={playerColor}
                        onChange={(color) => {
                            setPlayerColor(color as PlayerColor);
                            setSelectedPosition(INITIAL_POSITION);
                        }}
                        data={[
                            {
                                value: "w",
                                label: "White",
                            },
                            {
                                value: "b",
                                label: "Black",
                            },
                        ]}
                    />
                    <div className="mt-6">
                        <div className="mb-4 text-start">
                            <strong className="mb-8">Your {playerColor === "w" ? "white" : "black"} openings</strong>
                        </div>
                        <ScrollArea h={getScrollAreaHeight()} type="always">
                            <DragDropContext
                                onDragEnd={({ destination, source }) => {
                                    const fromIndex = state.findIndex((position) =>
                                        _.isEqual(openingsToDisplay.at(source.index), position),
                                    );
                                    const toIndex = destination?.index
                                        ? state.findIndex((position) =>
                                              _.isEqual(openingsToDisplay.at(destination.index), position),
                                          )
                                        : 0;
                                    setShouldSave(true);
                                    return handlers.reorder({ from: fromIndex, to: toIndex });
                                }}
                            >
                                <Droppable droppableId="dnd-list" direction="vertical">
                                    {(provided) => (
                                        <div {...provided.droppableProps} ref={provided.innerRef}>
                                            {items}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </DragDropContext>
                        </ScrollArea>
                    </div>
                </div>
                <div>
                    {isInitialPosition && !isExperiencedUser && (
                        <div className="mb-4 [@media(max-width:1023px)]:mt-12">
                            <Text c="dimmed">Make some moves to add new positions</Text>
                        </div>
                    )}
                    {(!isInitialPosition || isExperiencedUser) && (
                        <div className="my-4">
                            <Text size="lg" fw="bold">
                                {selectedPosition.name}
                            </Text>
                        </div>
                    )}
                    <div className={cn("w-full mb-10", { invisible: isInitialPosition && !isExperiencedUser })}>
                        <Button w="100%" h="4rem" onClick={() => openAddPositionModal()} color="green">
                            Add current position
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PositionsSidePanel;
